import zlib from 'zlib';
import { Buffer } from 'buffer';

/**
 * Generate a compressed and encoded report query from parameters
 * This follows the exact same pattern as ReportPage.vue
 */
export function generateReportQuery(parameters: any): string {
  try {
    // Build report configuration from the new direct parameter structure
    const reportQuery = buildReportConfig(parameters);

    // Convert to JSON with UTF-8 encoding (matching ReportPage.vue)
    const jsonString = JSON.stringify(reportQuery);

    // Convert string to UTF-8 buffer
    const buffer = Buffer.from(jsonString, 'utf8');

    // Compress using zlib deflate (not gzip!)
    const compressed = zlib.deflateSync(buffer);

    // Convert to base64 and make URL safe (matching ReportPage.vue exactly)
    const encodedString = compressed.toString('base64')
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

    return encodedString;
  } catch (error) {
    console.error('Error generating report query:', error);
    // Return a default query on error
    return generateDefaultQuery();
  }
}

/**
 * Build report configuration from the new direct parameter structure
 * This MUST match the exact structure from prepareReportOptions in ReportPage.vue
 */
function buildReportConfig(parameters: any): any {
  const timeRange = parameters.time_range || {};

  // Use filters directly as they're already in FilterColumnModel format from backend
  const filters = parameters.filters || [{
    name: 'order_status',
    code: 'order_status',
    dataType: 'string',
    operator: 'in',
    value: ['paid', 'partially_paid'],
  }];

  // Map measure to the format expected by ReportPage (add dash if needed)
  let measure = parameters.measure || 'sales_overview';
  if (measure === 'actual_inventory_quantity') {
    measure = 'Actual Inventory Quantity';
  }



  // Build report query matching ReportPage.vue prepareReportOptions() EXACTLY
  const reportQuery: any = {
    title: parameters.report_name || 'Custom Report',
    // chartType: 'bar',
    // hasChart: true,
    // displayMode: 'table',
    measure, // Must match measure.code format
    dimension: parameters.dimension || 'by_product', // Must match dimension.code format
    analyticsType: parameters.analyticsType || 'Top High', // Must match analyticsType.code format
    start_time: timeRange.from || null, // dates[0]
    end_time: timeRange.to || null, // dates[1]
    default_range: timeRange.period || '30d', // defaultRange
    filter: filters, // Must be FilterColumnModel format (this.filterColumns.items)
    // pivot_option: 'none', // pivotOption
    // params: [], // this.reportParams.items
    selected_report_columns: parameters.columns, // Use the extracted column codes
    page_options: {
      page: 1,
      pageSize: parameters.limit || 50,
      sortBy: [parameters.sort?.column],
      sortDesc: [parameters.sort?.direction === 'desc' ? true : false],
    },
      // product_segment_id: null, // this.productSegment.id
      // calculated_metrics: [], // this.calculatedMetrics.items
      // comparison_date: null, // this.comparisonDateModel
      // colorRules: [], // this.colorRules.items
      // pinColumnCount: 0 // this.pinColumnCount
  };

  return reportQuery;
}



/**
 * Generate a default query for fallback
 */
function generateDefaultQuery(): string {
  const defaultConfig = {
    title: 'Sales Overview',
    chartType: 'bar',
    hasChart: true,
    displayMode: 'table',
    measure: 'sales_overview',
    dimension: 'by_day',
    analyticsType: 'aggregate',
    start_time: null,
    end_time: null,
    default_range: '30d',
    filter: [
      {
        name: 'order_status',
        code: 'order_status',
        operator: 'in',
        value: ['paid', 'partially_paid'],
        dataType: 'string',
      },
    ],
    pivot_option: 'none',
    params: [],
    selected_report_columns: ['gross_sales', 'orders_count', 'units_sold'],
    page_options: {
      page: 1,
      pageSize: 50,
      sortBy: 'gross_sales',
      sortOrder: 'desc',
    },
    product_segment_id: null,
    calculated_metrics: [],
    comparison_date: null,
    colorRules: [],
    pinColumnCount: 0,
  };

  const jsonString = JSON.stringify(defaultConfig);
  const buffer = Buffer.from(jsonString, 'utf8');
  const compressed = zlib.deflateSync(buffer);
  const encodedString = compressed.toString('base64')
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

  return encodedString;
}

/**
 * Decode a report query back to parameters (for debugging)
 * This matches the decode logic in ReportPage.vue
 */
export function decodeReportQuery(encodedQuery: string): any {
  try {
    // Restore base64 padding
    const m = encodedQuery.length % 4;
    const reportQueryStr = encodedQuery
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(encodedQuery.length + (m === 0 ? 0 : 4 - m), '=');

    // Convert from base64 to buffer
    const buffer = Buffer.from(reportQueryStr, 'base64');

    // Decompress using zlib
    const decompressed = zlib.inflateSync(buffer);

    // Convert buffer to string using UTF-8 encoding
    const jsonString = decompressed.toString('utf8');

    // Parse the JSON
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Error decoding report query:', error);
    return null;
  }
}
